import React from 'react';
import { useLocation } from 'wouter';
import AdminLayout from './AdminLayout';
import CONFIG from '@/lib/config';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminCustomers from '@/pages/AdminCustomers';
import AdminUsers from '@/pages/AdminUsers';
import AdminACCUsers from '@/pages/AdminACCUsers';
import AdminAnalytics from '@/pages/AdminAnalytics';
import AdminAuditLogs from '@/pages/AdminAuditLogs';
import AdminReports from '@/pages/AdminReports';
import AdminNotifications from '@/pages/AdminNotifications';
import AdminSettings from '@/pages/AdminSettings';
import AdminHubs from '@/pages/AdminHubs';
import AdminProjects from '@/pages/AdminProjects';
import AdminDataSyncs from '@/pages/AdminDataSyncs';
import AdminAccessRequests from '@/pages/AdminAccessRequests';

const AdminRouter: React.FC = () => {
  const [location] = useLocation();
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);

  // Check admin authentication via session
  React.useEffect(() => {
    const checkAdminAuth = async () => {
      console.log('🔍 AdminRouter: Checking admin authentication...');
      console.log('🔍 AdminRouter: Current location:', location);
      
      try {
        // Check session via API
        const response = await fetch('/api/admin/user', {
          credentials: 'include' // Important for session cookies
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            console.log('✅ AdminRouter: Session found, authentication successful');
            setIsAuthenticated(true);
          } else {
            console.log('❌ AdminRouter: No valid session, redirecting to login');
            setIsAuthenticated(false);
            window.location.href = '/admin/login';
          }
        } else {
          console.log('❌ AdminRouter: Session check failed, redirecting to login');
          setIsAuthenticated(false);
          window.location.href = '/admin/login';
        }
      } catch (error) {
        console.error('❌ AdminRouter: Error checking session:', error);
        setIsAuthenticated(false);
        window.location.href = '/admin/login';
      }
    };

    checkAdminAuth();
  }, [location]); // Add location as dependency to re-check auth when route changes

  // Show loading while checking authentication
  console.log('🔍 AdminRouter render - isAuthenticated:', isAuthenticated, 'location:', location);
  
  if (isAuthenticated === null) {
    console.log('🔄 AdminRouter: Showing loading spinner');
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log('❌ AdminRouter: Not authenticated, returning null');
    return null;
  }

  console.log('✅ AdminRouter: Authenticated, rendering content');

  // Render the appropriate component based on the current path
  const renderContent = () => {
    // Handle exact matches first
    if (location === '/admin') {
        return <AdminDashboard />;
    }
    
    // Handle routes that start with specific paths
    if (location.startsWith('/admin/access-requests')) {
      return <AdminAccessRequests />;
    }
    if (location.startsWith('/admin/users') && location === '/admin/users') {
        return <AdminUsers />;
    }
    if (location.startsWith('/admin/acc-users') && location === '/admin/acc-users') {
        return <AdminACCUsers />;
    }
    if (location.startsWith('/admin/hubs') && location === '/admin/hubs') {
        return <AdminHubs />;
    }
    if (location.startsWith('/admin/projects')) {
        return <AdminProjects />;
    }
    if (location.startsWith('/admin/data-syncs') && location === '/admin/data-syncs') {
        return <AdminDataSyncs />;
    }
    if (location.startsWith('/admin/analytics') && location === '/admin/analytics') {
        return <AdminAnalytics />;
    }
    if (location.startsWith('/admin/audit-logs') && location === '/admin/audit-logs') {
        return <AdminAuditLogs />;
    }
    if (location.startsWith('/admin/reports') && location === '/admin/reports') {
        return <AdminReports />;
    }
    if (location.startsWith('/admin/notifications') && location === '/admin/notifications') {
        return <AdminNotifications />;
    }
    if (location.startsWith('/admin/settings') && location === '/admin/settings') {
        return <AdminSettings />;
    }
    
    // Default to dashboard
    return <AdminDashboard />;
  };

  return (
    <AdminLayout>
      {renderContent()}
    </AdminLayout>
  );
};

export default AdminRouter;
